import React, { useState, useEffect } from 'react';
import { 
  Button, 
  Dropdown, 
  Tag, 
  Badge,
  Space,
  Tooltip
} from 'antd';
import { 
  BarChartOutlined, 
  CheckCircleOutlined,
  SettingOutlined,
  DownOutlined
} from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';
import apiService from '../services/apiService';

const CompactScorecardSelector = ({ 
  projectId = null, 
  selectedScorecards, 
  onSelectionChange,
  style = {}
}) => {
  const [scorecards, setScorecards] = useState([]);
  const [loading, setLoading] = useState(false);
  const { getAuthToken } = useAuth();

  useEffect(() => {
    loadScorecards();
  }, [projectId]);

  const loadScorecards = async () => {
    if (!projectId) {
      console.log('No project ID provided to CompactScorecardSelector');
      setScorecards([]);
      return;
    }
    
    try {
      setLoading(true);
      const token = getAuthToken();
      console.log('Loading scorecards for project:', projectId);
      const response = await apiService.getAllScorecards(token, projectId);
      console.log('Scorecards API response:', response);
      
      // Handle different response formats
      const scorecardsData = response.data || response.scorecards || response || [];
      console.log('Parsed scorecards data:', scorecardsData);
      
      setScorecards(Array.isArray(scorecardsData) ? scorecardsData : []);
    } catch (error) {
      console.error('Failed to load scorecards:', error);
      console.error('Error details:', error.message);
      setScorecards([]);
    } finally {
      setLoading(false);
    }
  };

  const handleScorecardToggle = (scorecardNumber) => {
    const newSelection = selectedScorecards.includes(scorecardNumber)
      ? selectedScorecards.filter(n => n !== scorecardNumber)
      : [...selectedScorecards, scorecardNumber];
    
    onSelectionChange(newSelection);
  };

  const getOutcomeTypeIcon = (outcomeType) => {
    return outcomeType === 'score' ? 
      <BarChartOutlined style={{ color: '#1890ff', fontSize: '12px' }} /> : 
      <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '12px' }} />;
  };

  const menuItems = scorecards
    .sort((a, b) => String(a.scorecard_number).localeCompare(String(b.scorecard_number), undefined, { numeric: true }))
    .map(scorecard => {
    const isSelected = selectedScorecards.includes(scorecard.scorecard_number);
    
    return {
      key: scorecard.scorecard_number,
      label: (
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          minWidth: '200px',
          padding: '4px 0'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
            <Tag color="#8b5cf6" size="small">
              #{scorecard.scorecard_number}
            </Tag>
            <span style={{ fontSize: '12px' }}>
              {getOutcomeTypeIcon(scorecard.outcome_type)}
            </span>
            <span style={{ fontSize: '13px', color: '#fff', fontWeight: '500' }}>
              {scorecard.name}
            </span>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            {isSelected && <Badge status="success" />}
          </div>
        </div>
      ),
      onClick: () => handleScorecardToggle(scorecard.scorecard_number)
    };
  });

  // Add clear all option if any are selected
  if (selectedScorecards.length > 0) {
    menuItems.push({
      key: 'divider',
      type: 'divider'
    });
    menuItems.push({
      key: 'clear',
      label: (
        <div style={{ color: '#ff4d4f', fontSize: '12px' }}>
          Clear All
        </div>
      ),
      onClick: () => onSelectionChange([])
    });
  }

  const availableCount = scorecards.length;
  const selectedCount = selectedScorecards.length;
  console.log(scorecards)
  const getTooltipText = () => {
    if (!projectId) return 'Select a project first';
    if (loading) return 'Loading scorecards...';
    if (availableCount === 0) return 'No scorecards available for this project';
    return 'Click to select which scorecards to use for analysis';
  };

  return (
    <Tooltip title={getTooltipText()}>
      <Dropdown menu={{ items: menuItems }} trigger={["click"]} disabled={loading || availableCount === 0 || !projectId} placement="bottomRight">
        <Button
          size="small"
          loading={loading}
          style={{
            height: "100%",
            border: selectedCount > 0 ? "1px solid #8b5cf6" : "1px solid rgba(255, 255, 255, 0.2)",
            background: selectedCount > 0 ? "rgba(139, 92, 246, 0.1)" : "rgba(255, 255, 255, 0.05)",
            color: selectedCount > 0 ? "#8b5cf6" : "rgba(255, 255, 255, 0.7)",
            cursor: loading || availableCount === 0 || !projectId ? "not-allowed" : "pointer",
            ...style,
          }}
        >
          <Space size={4}>
            <SettingOutlined style={{ fontSize: "12px" }} />
            <span style={{ fontSize: "11px" }}>{!projectId ? "No Project" : availableCount === 0 ? "All Scorecards" : selectedCount === 0 ? "All Scorecards" : selectedCount === 1 ? `#${selectedScorecards[0]}` : `${selectedCount} Selected`}</span>
            {availableCount > 0 && projectId && <DownOutlined style={{ fontSize: "10px" }} />}
          </Space>
        </Button>
      </Dropdown>
    </Tooltip>
  );
};

export default CompactScorecardSelector;